import { useCallback, useEffect, useState } from 'react';

/** Seitengröße: Zahl oder 'alle' (kein Blättern). */
export type PageSize = number | 'alle';

export interface TableSettings {
  pageSize: PageSize;
  filters: Record<string, string>;
}

export interface UseTableSettingsResult {
  pageSize: PageSize;
  setPageSize: (size: PageSize) => void;
  filters: Record<string, string>;
  setFilter: (columnId: string, value: string) => void;
  resetFilters: () => void;
}

const STORAGE_PREFIX = 'wt.dt.';

function isValidPageSize(value: unknown): value is PageSize {
  if (value === 'alle') {
    return true;
  }
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isValidFilters(value: unknown): value is Record<string, string> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const rec = value as Record<string, unknown>;
  for (const key of Object.keys(rec)) {
    if (typeof rec[key] !== 'string') {
      return false;
    }
  }
  return true;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function loadSettings(
  storageKey: string | undefined,
  defaultPageSize: PageSize,
  persistFilters: boolean
): TableSettings {
  const fallback: TableSettings = { pageSize: defaultPageSize, filters: {} };
  if (!storageKey) {
    return fallback;
  }
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (raw === null) {
      return fallback;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) {
      return fallback;
    }
    const pageSize = isValidPageSize(parsed.pageSize) ? parsed.pageSize : defaultPageSize;
    const filters = persistFilters && isValidFilters(parsed.filters) ? parsed.filters : {};
    return { pageSize, filters };
  } catch {
    // Kein localStorage, kaputtes JSON, Quota: still auf Default.
    return fallback;
  }
}

/**
 * Filter- und Seitengrößen-Zustand einer Tabelle. Wenn `storageKey`
 * gesetzt ist, wird die Seitengröße (und bei `persistFilters` auch die
 * Filter) in localStorage unter `wt.dt.<storageKey>` gehalten.
 */
export function useTableSettings(options: {
  storageKey?: string | undefined;
  defaultPageSize: PageSize;
  persistFilters?: boolean | undefined;
}): UseTableSettingsResult {
  const storageKey = options.storageKey;
  const persistFilters = options.persistFilters === true;

  const [pageSize, setPageSize] = useState<PageSize>(
    () => loadSettings(options.storageKey, options.defaultPageSize, options.persistFilters === true).pageSize
  );
  const [filters, setFilters] = useState<Record<string, string>>(
    () => loadSettings(options.storageKey, options.defaultPageSize, options.persistFilters === true).filters
  );

  useEffect(() => {
    if (!storageKey) {
      return;
    }
    try {
      const payload: TableSettings = {
        pageSize,
        filters: persistFilters ? filters : {},
      };
      localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, JSON.stringify(payload));
    } catch {
      // Quota, privater Modus, fehlendes localStorage: still bleiben.
    }
  }, [storageKey, pageSize, persistFilters, filters]);

  const setFilter = useCallback((columnId: string, value: string) => {
    setFilters((prev) => {
      if (value === '') {
        if (!(columnId in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[columnId];
        return next;
      }
      if (prev[columnId] === value) {
        return prev;
      }
      return { ...prev, [columnId]: value };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFilters((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, []);

  return { pageSize, setPageSize, filters, setFilter, resetFilters };
}
